import { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import portalApi from '../lib/api';
import { RenewalStatusBadge } from '../components/RenewalStatusBadge';
import { RenewCaseDialog } from '../components/RenewCaseDialog';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../components/ui/select';
import { Search, RefreshCw, ArrowRight, Loader2, ChevronLeft } from 'lucide-react';

const PAGE_SIZE = 50;

const CARDS = [
  { key: 'eligible', label: 'Eligible for Renewal', cls: 'border-slate-200 bg-white hover:bg-slate-50', active: 'border-slate-500 bg-slate-100' },
  { key: 'pending', label: 'Pending Renewal', cls: 'border-orange-200 bg-orange-50 hover:bg-orange-100', active: 'border-orange-500 bg-orange-100' },
  { key: 'in_progress', label: 'In Progress', cls: 'border-amber-200 bg-amber-50 hover:bg-amber-100', active: 'border-amber-500 bg-amber-100' },
  { key: 'renewed', label: 'Renewed', cls: 'border-green-200 bg-green-50 hover:bg-green-100', active: 'border-green-500 bg-green-100' },
];

export default function RenewalTrackingPage() {
  const { hasPermission, activeWorkspaceId } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [years, setYears] = useState([]);
  const [offices, setOffices] = useState([]);
  const [managers, setManagers] = useState([]);
  const [data, setData] = useState({ items: [], total: 0, counts: {}, source_year: null, target_year: null });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState(searchParams.get('q') || '');
  const [renewTarget, setRenewTarget] = useState(null);

  const targetYearId = searchParams.get('target_year_id') || '';
  const status = searchParams.get('status') || 'eligible';
  const office = searchParams.get('office') || '';
  const caseManagerId = searchParams.get('case_manager_id') || '';
  const consent = searchParams.get('consent_status') || '';

  useEffect(() => { document.title = 'Renewal Tracking · Latinos Salud'; loadMeta(); }, []);

  useEffect(() => { if (targetYearId) load(); }, [searchParams, page, activeWorkspaceId]);

  const loadMeta = async () => {
    const [yearsData, constants, assignable] = await Promise.all([
      portalApi.getCoverageYears().catch(() => []),
      portalApi.getConstants().catch(() => null),
      portalApi.getAssignableUsers().catch(() => []),
    ]);
    const accessible = (yearsData || []).filter((y) => y.accessible !== false);
    setYears(accessible);
    setOffices(constants?.offices || []);
    setManagers(assignable || []);
    // Default the target year to the latest active year if none in the URL.
    if (!targetYearId && accessible.length) {
      const def = accessible.find((y) => y.is_active) || accessible[0];
      const p = new URLSearchParams(searchParams);
      p.set('target_year_id', def.id);
      setSearchParams(p, { replace: true });
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = {
        target_year_id: targetYearId,
        renewal_status: status === 'eligible' ? undefined : status,
        office: office || undefined,
        case_manager_id: caseManagerId || undefined,
        consent_status: consent || undefined,
        q: search || undefined,
        skip: page * PAGE_SIZE,
        limit: PAGE_SIZE,
        workspace_id: activeWorkspaceId || undefined,
      };
      setData(await portalApi.getRenewalTracking(params));
    } finally {
      setLoading(false);
    }
  };

  const setFilter = (key, value) => {
    const p = new URLSearchParams(searchParams);
    if (value && value !== 'all') p.set(key, value); else p.delete(key);
    setSearchParams(p);
    setPage(0);
  };

  const onSearch = (e) => { e.preventDefault(); setFilter('q', search); };

  const counts = data.counts || {};
  const totalPages = Math.ceil((data.total || 0) / PAGE_SIZE);
  const targetYear = useMemo(() => years.find((y) => y.id === targetYearId), [years, targetYearId]);

  return (
    <div className="space-y-6" data-testid="renewal-tracking-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <button onClick={() => navigate('/portal/cases')} className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1 mb-1" data-testid="back-to-cases">
            <ChevronLeft className="h-4 w-4" /> Cases
          </button>
          <h1 className="text-2xl font-bold text-slate-900">Renewal Tracking</h1>
          <p className="text-slate-500 mt-1">
            Cycle {data.source_year || '—'} → {data.target_year || '—'} · one row per eligible patient
          </p>
        </div>
        <Select value={targetYearId} onValueChange={(v) => setFilter('target_year_id', v)}>
          <SelectTrigger className="w-[220px]" data-testid="tracking-year-select">
            <SelectValue placeholder="Target year" />
          </SelectTrigger>
          <SelectContent>
            {years.filter((y) => y.is_active).map((y) => <SelectItem key={y.id} value={y.id}>{y.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Clickable status cards; the active one filters the list below. */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {CARDS.map((c) => (
          <button key={c.key} type="button"
            onClick={() => setFilter('status', c.key)}
            className={`text-left rounded-xl p-4 border transition-colors cursor-pointer ${status === c.key ? c.active : c.cls}`}
            data-testid={`track-card-${c.key}`}>
            <p className="text-xs font-medium uppercase tracking-wider text-slate-600">{c.label}</p>
            <p className="text-2xl font-bold text-slate-900 mt-1">{counts[c.key] ?? 0}</p>
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl p-4 border border-slate-200 space-y-4">
        <form onSubmit={onSearch} className="w-full">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
            <Input placeholder="Search by patient name or Athena ID..." value={search}
              onChange={(e) => setSearch(e.target.value)} className="pl-10 w-full" data-testid="tracking-search" />
          </div>
        </form>
        <div className="flex flex-wrap gap-3">
          <Select value={office || 'all'} onValueChange={(v) => setFilter('office', v)}>
            <SelectTrigger className="w-[180px]" data-testid="tracking-office-filter"><SelectValue placeholder="All Offices" /></SelectTrigger>
            <SelectContent className="max-h-60">
              <SelectItem value="all">All Offices</SelectItem>
              {offices.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={caseManagerId || 'all'} onValueChange={(v) => setFilter('case_manager_id', v)}>
            <SelectTrigger className="w-[200px]" data-testid="tracking-manager-filter"><SelectValue placeholder="All Case Managers" /></SelectTrigger>
            <SelectContent className="max-h-60">
              <SelectItem value="all">All Case Managers</SelectItem>
              {managers.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={consent || 'all'} onValueChange={(v) => setFilter('consent_status', v)}>
            <SelectTrigger className="w-[180px]" data-testid="tracking-consent-filter"><SelectValue placeholder="All Consent" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Consent</SelectItem>
              <SelectItem value="Consent Received">Consent Received</SelectItem>
              <SelectItem value="Consent Pending">Consent Pending</SelectItem>
              <SelectItem value="Consent Declined">Consent Declined</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {['Athena ID', 'Patient Name', 'Office', 'Case Manager', 'Source Year', 'Target Year', 'Renewal Status', ''].map((h, i) => (
                  <th key={i} className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {loading ? (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></td></tr>
              ) : data.items.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400" data-testid="tracking-empty">No patients in this bucket.</td></tr>
              ) : data.items.map((p) => (
                <tr key={p.source_case_id} className="hover:bg-slate-50" data-testid={`tracking-row-${p.source_case_id}`}>
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-sky-600">
                    <Link to={`/portal/cases/${p.source_case_id}`} className="hover:underline">{p.athena_id || 'Open case'}</Link>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-700">{p.patient_name || '—'}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600">{p.office || '—'}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600">{p.case_manager_name || '—'}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600">{p.source_coverage_year}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600">{p.target_coverage_year}</td>
                  <td className="px-4 py-3 whitespace-nowrap"><RenewalStatusBadge status={p.renewal_status} /></td>
                  <td className="px-4 py-3 whitespace-nowrap text-right">
                    {p.renewal_case_id ? (
                      <Button variant="ghost" size="sm" asChild data-testid={`tracking-open-${p.source_case_id}`}>
                        <Link to={`/portal/cases/${p.renewal_case_id}`}>Open {p.target_coverage_year} <ArrowRight className="h-4 w-4 ml-1" /></Link>
                      </Button>
                    ) : hasPermission('agent.producer') ? (
                      <Button variant="outline" size="sm" className="border-orange-300 text-orange-700 hover:bg-orange-50"
                        onClick={() => setRenewTarget({ id: p.source_case_id, athena_id: p.athena_id, coverage_year_label: `${p.source_coverage_year}` })}
                        data-testid={`tracking-renew-${p.source_case_id}`}>
                        <RefreshCw className="h-4 w-4 mr-1" /> Renew
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
          <p className="text-sm text-slate-500">{data.total} patients</p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>Previous</Button>
            <span className="text-sm text-slate-500">Page {page + 1} of {Math.max(1, totalPages)}</span>
            <Button variant="outline" size="sm" disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</Button>
          </div>
        </div>
      </div>

      {renewTarget && (
        <RenewCaseDialog
          caseItem={renewTarget}
          open={!!renewTarget}
          onOpenChange={(v) => { if (!v) setRenewTarget(null); }}
          onRenewed={() => { setRenewTarget(null); load(); }}
        />
      )}
    </div>
  );
}
